import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  CreatePickupBody,
  DeletePickupResponse,
  PickupDetail,
  PickupListResponse,
  PickupStats,
  Stage,
  TransitionResponse,
} from './types'

const KEY = ['material-pickup'] as const

export interface PickupListFilters {
  status?: Stage
  vendor?: string
  archived?: boolean
  includeDeleted?: boolean
  limit?: number
  offset?: number
}

export function usePickupList(filters: PickupListFilters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.vendor) params.set('vendor', filters.vendor)
  if (filters.archived) params.set('archived', 'true')
  if (filters.includeDeleted) params.set('include_deleted', 'true')
  params.set('limit', String(filters.limit ?? 100))
  params.set('offset', String(filters.offset ?? 0))
  const qs = params.toString()

  return useQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: () => jsonFetch<PickupListResponse>(`/api/material-pickup?${qs}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

export function usePickupStats() {
  return useQuery({
    queryKey: [...KEY, 'stats'],
    queryFn: () => jsonFetch<PickupStats>('/api/material-pickup/stats'),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })
}

export function usePickupDetail(pickupId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', pickupId],
    queryFn: () => jsonFetch<PickupDetail>(`/api/material-pickup/${pickupId}`),
    enabled: !!pickupId,
    staleTime: 10_000,
  })
}

export function useTransitionPickup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      pickupId,
      targetStage,
      note,
    }: {
      pickupId: string
      targetStage: Stage
      note?: string
    }) =>
      jsonFetch<TransitionResponse>(`/api/material-pickup/${pickupId}/transition`, {
        method: 'POST',
        body: { target_stage: targetStage, note },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function usePatchPickup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pickupId, edits }: { pickupId: string; edits: Record<string, unknown> }) =>
      jsonFetch<{ id: string }>(`/api/material-pickup/${pickupId}`, {
        method: 'PATCH',
        body: edits,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useCreatePickup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreatePickupBody) =>
      jsonFetch<PickupDetail>('/api/material-pickup/upload', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useLinkPo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pickupId, poReference }: { pickupId: string; poReference: string }) =>
      jsonFetch(`/api/material-pickup/${pickupId}/link-po`, {
        method: 'POST',
        body: { po_reference: poReference },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useArchivePickup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pickupId, reason }: { pickupId: string; reason?: string }) =>
      jsonFetch<DeletePickupResponse>(`/api/material-pickup/${pickupId}`, {
        method: 'DELETE',
        body: reason ? { reason } : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useRestorePickup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pickupId: string) =>
      jsonFetch<PickupDetail>(`/api/material-pickup/${pickupId}/restore`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export function useRetryExtraction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pickupId: string) =>
      jsonFetch(`/api/material-pickup/${pickupId}/extract`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

export interface RegisterExternalFreightBody {
  odoo_po_name: string
  carrier_name: string
  provider: string
  provider_ref: string
  bol_number?: string
  pickup_date?: string
  est_delivery?: string
  notes?: string
  material_pickup_id?: string
}

export interface RegisterExternalFreightResult {
  shipment_id: number
  status: string
  live_tracking: boolean
}

export function useRegisterExternalFreight() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RegisterExternalFreightBody) =>
      jsonFetch<RegisterExternalFreightResult>('/api/freight/register-external', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['freight-shipments'] })
    },
  })
}
